import React, { FC, useState, useEffect } from 'react';
import { Fade, Grid, Box, TextField, Autocomplete } from '@mui/material';
import { Modal, Loader, Card, ModalSaveSection } from '../../../components';
import { useSnackbar } from 'notistack';
import { IDropdownResponse } from '../../../models';
import { Assignment } from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import { getNeighborhoodLookup, createJob } from '../../../fetch';

interface ICreateJobModal {
  open: boolean;
  onClose: () => void;
}
export const CreateJobModal: FC<ICreateJobModal> = ({ open, onClose }) => {
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingNeighborhoods, setLoadingNeighborhoods] = useState(true);
  const [neighborhoodLookup, setNeighborhoodLookup] = useState<IDropdownResponse[]>([]);
  const [lot, setLot] = useState<number | string>('');
  const [selectedNeighborhood, setSelectedNeighborhood] = useState<number | string>('');
  const [selectedNeighborhoodId, setSelectedNeighborhoodId] = useState<number | string>('');
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const handleInput = event => {
    const inputValue = event.target.value;
    const truncatedValue = inputValue.slice(0, 3);
    setLot(truncatedValue);
  };

  const handleClose = () => {
    setSelectedNeighborhood('');
    setLot('');
    onClose();
  };

  const fetchNeighborhoods = async () => {
    try {
      setLoadingNeighborhoods(true);
      const res = await getNeighborhoodLookup();
      setNeighborhoodLookup(res);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingNeighborhoods(false);
    }
  };

  useEffect(() => {
    fetchNeighborhoods();
  }, []);

  return (
    <>
      <Modal
        title="Add New Job"
        open={open}
        onClose={() => {
          handleClose();
        }}
        maxWidth="md"
      >
        {isSaving && <Loader type="overlay" position="centered" />}
        <Fade in={open}>
          <Box mt={1}>
            <Card title="Info" icon={<Assignment />}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Autocomplete
                    disabled={isLoadingNeighborhoods}
                    value={selectedNeighborhood}
                    onChange={(event, newValue: string) => {
                      const selected = neighborhoodLookup.find(
                        neighborhood => neighborhood.description === newValue
                      );
                      if (selected) {
                        setSelectedNeighborhood(selected.description);
                        setSelectedNeighborhoodId(selected.value);
                      }
                    }}
                    id="name"
                    options={neighborhoodLookup.map(neighborhood => neighborhood.description)}
                    getOptionLabel={(option: string) => {
                      return option;
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        size="small"
                        name="name"
                        autoComplete="none"
                        label="Name"
                        variant="outlined"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    type="number"
                    fullWidth
                    variant="outlined"
                    autoComplete="nope"
                    label="Lot"
                    name="lot"
                    size="small"
                    value={lot}
                    onInput={handleInput}
                  />
                </Grid>
              </Grid>
            </Card>
            <ModalSaveSection
              isSaveDisabled={!selectedNeighborhood || !lot}
              handleCancel={() => {
                handleClose();
              }}
              handleSave={async () => {
                try {
                  setIsSaving(true);
                  const job = await createJob(selectedNeighborhoodId, Number(lot));

                  enqueueSnackbar('Success, job created!', {
                    variant: 'success'
                  });

                  handleClose();
                  history.push(`/development/jobs/${job.data}/general-information`);
                } catch (error) {
                  enqueueSnackbar(error?.Detail ?? `Error creating job, please try again.`, {
                    variant: 'error'
                  });
                  console.log(error);
                } finally {
                  setIsSaving(false);
                }
              }}
            />
          </Box>
        </Fade>
      </Modal>
    </>
  );
};
